import React, { useState, useEffect } from 'react';
// @ts-ignore - react-dom types not available  
import { createPortal } from 'react-dom';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: {
    type: 'text' | 'image';
    textContent: string;
    imageUrl: string;
  };
}

export const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose, title, content }) => {
  const [imageLoadError, setImageLoadError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  // Reset error state when modal opens or content changes
  useEffect(() => {
    if (isOpen) {
      setImageLoadError(false);
      // Show animation
      const showTimer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(showTimer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, content.imageUrl]);

  
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className={`
        fixed inset-0 flex items-center justify-center
        transition-all duration-300 ease-out
        ${isVisible ? 'opacity-100' : 'opacity-0'}
        p-4 sm:p-6 md:p-8
      `}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1050,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(2px)',
        overflowY: 'auto'
      }}
    >
      {/* Modal */}
      <div 
        className={`
          relative bgc-4 bdr-10 bx-sd
          transform transition-all duration-300 ease-out
          ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-8'}
          mx-auto flex flex-col
        `}
        style={{
          maxWidth: '700px',
          width: '95%',
          height: 'auto',
          maxHeight: '85vh',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          zIndex: 1060,
          border: '3px solid #00a16f',
          borderRadius: '16px',
          position: 'relative',
          margin: 'auto',
          background: 'linear-gradient(135deg, #2d3436 0%, #636e72 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Close button - copied from PopupModal.tsx */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="bgc-15"
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            padding: '0',
            color: 'white',
            fontSize: '16px',
            border: '2px solid rgba(255,255,255,0.8)',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            fontWeight: 'bold',
            backgroundColor: '#00a16f',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#004c3d'
            e.currentTarget.style.transform = 'scale(1.1)'
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#00a16f'
            e.currentTarget.style.transform = 'scale(1)'
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)'
          }}
        >
          ×
        </button>

        {/* Header */}
        <div 
          className="flex items-center p-6"
          style={{
            background: 'linear-gradient(135deg, #00a16f 0%, #00b894 100%)',
            borderRadius: '16px 16px 0 0',
            flexShrink: 0
          }}
        >
          <h3 className="text-xl font-bold text-white flex items-center">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
              <i className="fas fa-info-circle text-white text-sm"></i>
            </div>
            {title}
          </h3>
        </div>
        
        {/* Content */}
        <div 
          className="overflow-y-auto flex-1"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '0 0 16px 16px',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            scrollBehavior: 'smooth',
            minHeight: 0
          }}
        >
          <div className="p-6">
          {content.type === 'text' || (content.type === 'image' && (!content.imageUrl || imageLoadError)) ? (
            // แสดงข้อความ (เป็น type text หรือ type image แต่รูปโหลดไม่ได้)
            <div 
              className="text-gray-800 leading-relaxed prose prose-sm max-w-none"
              style={{
                fontSize: '14px',
                lineHeight: '1.7',
                color: '#2d3436'
              }}
              dangerouslySetInnerHTML={{ 
                __html: content.textContent || '<p style="color: #636e72; font-style: italic; text-align: center; padding: 2rem;">ยังไม่มีข้อมูล</p>' 
              }}
            />
          ) : (
            // แสดงรูปภาพ (type image และมี URL)
            <div className="text-center">
              <div className="relative inline-block">
                <img 
                  src={content.imageUrl} 
                  alt={title}
                  className="max-w-full h-auto mx-auto rounded-xl shadow-2xl"
                  style={{
                    maxHeight: 'calc(90vh - 200px)',
                    objectFit: 'contain',
                    border: '3px solid rgba(0, 161, 111, 0.2)'
                  }}
                  onError={() => setImageLoadError(true)}
                  onLoad={() => setImageLoadError(false)}
                />
              </div>
              {/* Fallback: ถ้ารูปโหลดไม่ได้และไม่มี textContent */}
              {imageLoadError && !content.textContent && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                  <p className="text-red-600 italic text-center">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    ไม่สามารถโหลดรูปภาพได้
                  </p>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
